import React, { useState } from 'react';
import {
  Card,
  Steps,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Typography,
  Timeline,
  Tag,
  Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  ExclamationCircleOutlined,
  EditOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useSubmitQualityPlanForApproval,
  useApproveQualityPlan,
  useRejectQualityPlan,
} from '../../hooks/useQuality';
import { useUserDisplayResolver } from '@/hooks/useUserDisplayResolver';
import { RejectionReasonModal } from '@/components/rejection/RejectionReasonModal';
import type { RejectionPayload } from '@/services/rejection.service';

const { Text } = Typography;
const { TextArea } = Input;

interface QualityPlanApprovalWorkflowProps {
  qualityPlan: any;
  canApprove?: boolean;
  onSuccess?: () => void;
}

export const QualityPlanApprovalWorkflow: React.FC<QualityPlanApprovalWorkflowProps> = ({
  qualityPlan,
  canApprove = false,
  onSuccess,
}) => {
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
  const [approveForm] = Form.useForm();

  const submitForApproval = useSubmitQualityPlanForApproval();
  const approveQualityPlan = useApproveQualityPlan();
  const rejectQualityPlan = useRejectQualityPlan();
  const { resolve } = useUserDisplayResolver();

  const getStepStatus = (status: string) => {
    const statusMap: Record<string, number> = {
      'Draft': 0,
      'Under Review': 1,
      'Approved': 2,
      'Active': 3,
    };
    return statusMap[status] || 0;
  };

  const handleSubmitForApproval = () => {
    Modal.confirm({
      title: 'Submit for Approval',
      content: 'Are you sure you want to submit this quality plan for approval?',
      icon: <SendOutlined />,
      onOk: async () => {
        try {
          await submitForApproval.mutateAsync(qualityPlan.id);
          onSuccess?.();
        } catch (error) {
          console.error('Error submitting for approval:', error);
        }
      },
    });
  };

  const handleApprove = async (values: any) => {
    try {
      await approveQualityPlan.mutateAsync({
        id: qualityPlan.id,
        notes: values.notes,
      });
      setIsApproveModalVisible(false);
      approveForm.resetFields();
      onSuccess?.();
    } catch (error) {
      console.error('Error approving quality plan:', error);
    }
  };

  const handleRejectConfirm = async (payload: RejectionPayload) => {
    try {
      await rejectQualityPlan.mutateAsync({
        id: qualityPlan.id,
        payload,
      });
      setIsRejectModalVisible(false);
      onSuccess?.();
    } catch (error) {
      console.error('Error rejecting quality plan:', error);
    }
  };

  return (
    <Card title="Approval Workflow" size="small">
      <Steps
        current={getStepStatus(qualityPlan.status)}
        items={[
          {
            title: 'Draft',
            description: 'Plan creation',
            icon: <EditOutlined />,
          },
          {
            title: 'Under Review',
            description: 'Awaiting approval',
            icon: <ExclamationCircleOutlined />,
          },
          {
            title: 'Approved',
            description: 'Plan approved',
            icon: <CheckCircleOutlined />,
          },
          {
            title: 'Active',
            description: 'In use',
            icon: <CheckCircleOutlined />,
          },
        ]}
        style={{ marginBottom: 24 }}
      />

      {/* Action Buttons */}
      <Space style={{ marginBottom: 16 }}>
        {qualityPlan.status === 'Draft' && (
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSubmitForApproval}
            loading={submitForApproval.isPending}
          >
            Submit for Approval
          </Button>
        )}

        {qualityPlan.status === 'Under Review' && canApprove && (
          <>
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setIsApproveModalVisible(true)}
              loading={approveQualityPlan.isPending}
            >
              Approve
            </Button>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              onClick={() => setIsRejectModalVisible(true)}
              loading={rejectQualityPlan.isPending}
            >
              Reject
            </Button>
          </>
        )}
      </Space>

      {/* Approval History */}
      {(qualityPlan.approval_date || qualityPlan.approval_notes) && (
        <>
          <Divider>Approval History</Divider>
          <Timeline
            items={[
              qualityPlan.created_at && {
                color: 'blue',
                children: (
                  <div>
                    <Text strong>Created</Text>
                    <br />
                    <Text type="secondary">
                      {dayjs(qualityPlan.created_at).format('DD MMM YYYY HH:mm')}
                    </Text>
                    {qualityPlan.created_by && (
                      <>
                        <br />
                        <Text type="secondary">by {resolve(qualityPlan.created_by)}</Text>
                      </>
                    )}
                  </div>
                ),
              },
              qualityPlan.approval_date && {
                color: 'green',
                children: (
                  <div>
                    <Text strong>Approved</Text>
                    <br />
                    <Text type="secondary">
                      {dayjs(qualityPlan.approval_date).format('DD MMM YYYY HH:mm')}
                    </Text>
                    {qualityPlan.approved_by && (
                      <>
                        <br />
                        <Text type="secondary">by {resolve(qualityPlan.approved_by)}</Text>
                      </>
                    )}
                    {qualityPlan.approval_notes && (
                      <>
                        <br />
                        <Text type="secondary">{qualityPlan.approval_notes}</Text>
                      </>
                    )}
                  </div>
                ),
              },
              qualityPlan.effective_date && {
                color: 'blue',
                children: (
                  <div>
                    <Text strong>Effective</Text>
                    <br />
                    <Text type="secondary">
                      {dayjs(qualityPlan.effective_date).format('DD MMM YYYY')}
                    </Text>
                  </div>
                ),
              },
            ].filter(Boolean)}
          />
        </>
      )}

      {/* Approve Modal */}
      <Modal
        title="Approve Quality Plan"
        open={isApproveModalVisible}
        onCancel={() => {
          setIsApproveModalVisible(false);
          approveForm.resetFields();
        }}
        footer={null}
      >
        <Form form={approveForm} layout="vertical" onFinish={handleApprove}>
          <Form.Item name="notes" label="Approval Notes (Optional)">
            <TextArea rows={4} placeholder="Enter any approval notes or comments" />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={approveQualityPlan.isPending}
              >
                Approve
              </Button>
              <Button
                onClick={() => {
                  setIsApproveModalVisible(false);
                  approveForm.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <RejectionReasonModal
        open={isRejectModalVisible}
        title="Reject Quality Plan"
        documentType="quality_plan"
        documentLabel={qualityPlan?.plan_number ? `Plan ${qualityPlan.plan_number}` : undefined}
        confirmLoading={rejectQualityPlan.isPending}
        onCancel={() => setIsRejectModalVisible(false)}
        onConfirm={handleRejectConfirm}
      />
    </Card>
  );
};
